import { Illus, lgBumps, LinkAnchor, Notification, Typo } from '@wrisk/ui-components'
import { DateTime } from 'luxon'
import React, { FunctionComponent, useCallback } from 'react'
import { useNavigate } from 'react-router'

import { getCoreAsset, getCurrentQuote } from '../../../../domain'
import { useAuth } from '../../../../hooks/auth'
import { useErrorHandlingAsyncCallback } from '../../../../hooks/errors'
import {
  tFormats,
  TKey,
  useWriskTranslation,
} from '../../../../infrastructure/internationalisation'
import { toDateTime } from '../../../../util/date'
import { AppPath } from '../../../AppPath'
import { usePrincipal } from '../../../authentication'
import { Feedback } from '../../../organisms/feedback/Feedback'
import { WriskTrans } from '../../../organisms/trans'
import { FullPage } from '../../../templates'
import { useProposal } from '../ProposalProvider'

const tKey = TKey('pages.activation')

export const PolicyActivation: FunctionComponent = () => {
  const navigate = useNavigate()

  const { t } = useWriskTranslation()

  const auth = useAuth()

  const { proposal } = useProposal()
  const { isAuthenticated } = usePrincipal()

  const onContinue = useCallback(() => navigate(AppPath.HOME), [navigate])
  const onSignInCallback = useErrorHandlingAsyncCallback(() => auth.signIn(AppPath.HOME))

  const startAt = proposal.startAt

  const quote = getCurrentQuote(proposal)
  const asset = getCoreAsset(quote)
  const newActivation = asset.data.activation?.transactionCode === 'NEW_REGISTRATION'

  const contentKey = newActivation ? 'activation-content' : 'switch-content'
  const contentDeferredKey = newActivation ? 'activation-deferred' : 'switch-deferred'

  const content =
    startAt && DateTime.fromISO(startAt) > DateTime.local() ? (
      <WriskTrans
        t={t}
        i18nKey={tKey(contentDeferredKey)}
        values={{
          startAt: t(tFormats('date.medium'), { value: toDateTime(startAt) }),
        }}
      />
    ) : (
      <WriskTrans t={t} i18nKey={tKey(contentKey)} />
    )

  const activationBanner = newActivation ? (
    <Notification flexDirection='column' rowGap={1} justifyContent='center' mb={lgBumps}>
      <Typo>{t(tKey('activation-code-label'))}</Typo>
      <Typo typoSize='lg' fontWeight='bold'>
        {asset.data.activation?.evbNumber}
      </Typo>
    </Notification>
  ) : undefined

  const actionToRender = isAuthenticated ? (
    <LinkAnchor onClick={onContinue} variant='standalone'>
      {t(tKey('actions.continue'))}
    </LinkAnchor>
  ) : (
    <LinkAnchor onClick={onSignInCallback.execute} variant='standalone'>
      {t(tKey('actions.sign-in'))}
    </LinkAnchor>
  )

  return (
    <FullPage
      pageId='payment-page'
      header={t(tKey('header'))}
      upperHeader={<Illus alt='Success' data-testid='bind-success' illus='bindSuccess' />}
    >
      <Typo as='pre' mb={lgBumps}>
        {content}
      </Typo>
      {activationBanner}
      {actionToRender}

      <Feedback type='bind' tags={{ policyId: proposal.policyId ?? '' }} />
    </FullPage>
  )
}
